<h3>AI Upload</h3>

<p>
  Upload one or more PDFs, Word documents (.docx), or images to your current AI workspace.
  Images are run through OCR (Azure Document Intelligence); the extracted text is what gets indexed for Q&amp;A.
  When you’re ready to ask questions, go to <a routerLink="/jfriedman/ai-qa">AI Q&amp;A</a>.
  <b>Content generated by AI. Please verify important information independently.</b>
</p>

<div class="workspace-files-panel card mb-3">
  <div class="card-header d-flex justify-content-between align-items-center">
    <span>Files in your current workspace <span class="text-muted small">(from app database)</span></span>
    <button type="button"
            class="btn btn-sm btn-outline-secondary"
            (click)="loadWorkspaceFiles()"
            [disabled]="busyListingWorkspace">
      <span *ngIf="busyListingWorkspace"><i class="fas fa-spinner fa-spin"></i></span>
      <span *ngIf="!busyListingWorkspace"><i class="fas fa-sync-alt"></i> Refresh</span>
    </button>
  </div>
  <div class="card-body">
    <div *ngIf="workspaceListError" class="alert alert-warning py-2 mb-3">{{ workspaceListError }}</div>
    <p *ngIf="!workspaceFiles?.vectorStoreId && !workspaceListError" class="text-muted mb-0">
      No active workspace yet. Upload documents to create one.
    </p>
    <ng-container *ngIf="workspaceFiles?.vectorStoreId">
      <p class="small text-muted mb-2"><b>Vector store</b>: {{ workspaceFiles.vectorStoreId }}</p>
      <div *ngIf="!workspaceFiles.files?.length" class="text-muted">No files recorded for this workspace.</div>
      <div class="table-responsive" *ngIf="workspaceFiles.files?.length">
        <table class="table table-sm table-striped mb-0">
          <thead>
            <tr>
              <th>Name</th>
              <th>Size</th>
              <th>Uploaded (UTC)</th>
              <th>File id</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let wf of workspaceFiles.files">
              <td>{{ wf.originalFileName }}</td>
              <td>{{ formatSize(wf.sizeBytes) }}</td>
              <td>{{ formatUploaded(wf.uploadedUtc) }}</td>
              <td class="small text-break">{{ wf.fileId }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </ng-container>
  </div>
</div>

<div class="toolbar">
  <label class="append-toggle">
    <input type="checkbox" [(ngModel)]="appendCurrent" />
    Append to current workspace (recommended)
  </label>
</div>

<div class="upload-container">
  <div class="upload-area"
       (dragover)="onDragOver($event)"
       (dragleave)="onDragLeave($event)"
       (drop)="onFilesDropped($event)"
       [class.has-file]="selectedFiles.length > 0">

    <div *ngIf="selectedFiles.length === 0" class="upload-placeholder">
      <i class="fas fa-cloud-upload-alt fa-3x"></i>
      <p>Drop PDFs, Word files, or images here or click to browse</p>
      <input type="file"
             accept=".pdf,.docx,.png,.jpg,.jpeg,.gif,.bmp,.tif,.tiff,.webp,.heic,image/*"
             multiple
             (change)="onFilesSelected($event)"
             style="display: none;"
             #fileInput>
      <button class="btn btn-secondary" (click)="fileInput.click()">Choose files</button>
    </div>

    <div *ngIf="selectedFiles.length > 0" class="file-selected">
      <div class="file-row" *ngFor="let f of selectedFiles; let i = index">
               <div class="file-meta">
          <i class="fas fa-lg"
             [ngClass]="f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf') ? 'fa-file-pdf'
               : (f.name.toLowerCase().endsWith('.docx') || f.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') ? 'fa-file-word'
               : 'fa-file-image'"></i>
          <span class="file-name"><b>{{ f.name }}</b></span>
          <span class="file-size">{{ (f.size / 1024 / 1024).toFixed(2) }} MB</span>
        </div>
        <button class="btn btn-sm btn-outline-secondary" (click)="removeFile(i)" [disabled]="busyUploading">Remove</button>
      </div>

      <div class="file-actions">
        <button class="btn btn-secondary" (click)="clearAll()" [disabled]="busyUploading">Clear</button>
        <button class="btn btn-secondary" (click)="fileInput.click()" [disabled]="busyUploading">Add more</button>
      </div>
    </div>
  </div>

  <div *ngIf="uploadError" class="alert alert-danger">
    {{ uploadError }}
  </div>

  <div *ngIf="deleteError" class="alert alert-danger">
    {{ deleteError }}
  </div>

  <div class="action-buttons">
    <button class="btn btn-primary"
            [disabled]="selectedFiles.length === 0 || busyUploading"
            (click)="upload()">
      <span *ngIf="busyUploading">
        <i class="fas fa-spinner fa-spin"></i> Uploading...
      </span>
      <span *ngIf="!busyUploading">
        <i class="fas fa-upload"></i> Upload
      </span>
    </button>
  </div>
</div>

<div *ngIf="response" class="result">
  <h4>Upload complete</h4>
  <div><b>Vector store</b>: {{ response.vectorStoreId }}</div>
  <div><b>Files</b>:</div>
  <ul>
    <li *ngFor="let id of response.fileIds">{{ id }}</li>
  </ul>
</div>

<div *ngIf="cleanupResponse" class="result">
  <h4>Cleanup</h4>
  <div>{{ cleanupResponse.message }}</div>
  <div *ngIf="cleanupResponse.vectorStoreId"><b>Vector store</b>: {{ cleanupResponse.vectorStoreId }}</div>
  <div *ngIf="cleanupResponse.fileIds?.length">
    <b>Files</b>:
    <ul>
      <li *ngFor="let id of cleanupResponse.fileIds">{{ id }}</li>
    </ul>
  </div>
</div>

<div class="danger-zone">
  <h4>Delete Workspace</h4>
  <p>
    This permanently deletes your current AI workspace (vector store + uploaded files).
    You’ll need to re-upload documents to ask questions again.
  </p>

  <button class="btn btn-danger btn-lg"
          [disabled]="busyUploading || busyDeleting"
          (click)="deleteWorkspace()">
    <span *ngIf="busyDeleting">
      <i class="fas fa-spinner fa-spin"></i> Deleting workspace...
    </span>
    <span *ngIf="!busyDeleting">
      <i class="fas fa-trash"></i> Delete workspace
    </span>
  </button>
</div>

